import { ISelectOption } from '@constants/entities/ui';
import { DomainNode } from '@store/services/nodes/types';
import { IDomain } from '@store/services/projects/types';
import { findWideDomain } from '@utils/domains';
import { ITreeItem } from '@views/Project/helpers';

export const getAutoSelectedDomain = (
  selectedNodeId: string,
  projectDomains: IDomain[],
  tree: ITreeItem[],
  domainNodes: DomainNode[],
  currentValue: ISelectOption[],
): ISelectOption | undefined => {
  const subDomain = projectDomains.find((d) => d.id === selectedNodeId);

  // Check is selected node is sub-domain
  if (subDomain?.level === 1) {
    const parentId = subDomain?.parentId;

    // Check is parent domain NOT selected
    if (!currentValue.some((d) => d.value === parentId)) {
      const requiredSubDomains = tree.find((t) => t.id === parentId)?.children;

      if (requiredSubDomains) {
        const isAllRequiredSubDomainsSelected = requiredSubDomains.every(
          (requiredDomain) =>
            currentValue.some((domain) => domain.value === requiredDomain.id) ||
            requiredDomain.id === selectedNodeId,
        );

        // Check is all required sub-domains are selected
        if (isAllRequiredSubDomainsSelected) {
          const parentName = tree.find((t) => t.id === parentId)?.name;

          // Return parent domain to be auto-selcted
          return {
            value: parentId,
            label: parentName ?? parentId,
            type:
              domainNodes.find((dn) => dn.domain_id === parentId)?.type ||
              'direct',
            isAutoSelected: true,
          };
        }
      }
    }
  }
};

type CheckIsAllDomainsSelectedParams = {
  selectedDomains: ISelectOption[];
  projectDomains: IDomain[];
};

export const checkIsAllDomainsSelected = ({
  selectedDomains,
  projectDomains,
}: CheckIsAllDomainsSelectedParams) => {
  const wideDomain = findWideDomain(projectDomains);

  const isAllDomainsSelected = projectDomains.every(
    ({ id }) =>
      wideDomain?.id === id ||
      selectedDomains.some(({ value }) => value === id),
  );

  const isWideSelected = selectedDomains.some(
    ({ value }) => value === wideDomain?.id,
  );

  return { isAllDomainsSelected, isWideSelected };
};

export type GetSelectedDomainsParams = {
  selectedDomains: ISelectOption[] | undefined;
  wideDomain: ITreeItem | undefined;
};

export const getSelectedDomains = ({
  selectedDomains,
  wideDomain,
}: GetSelectedDomainsParams) => {
  const isWideSelected = selectedDomains?.some(
    ({ value }) => value === wideDomain?.id,
  );

  if (isWideSelected) {
    return (
      wideDomain?.children
        ?.map(({ id, children }) => [
          id,
          ...(children?.map(({ id: childId }) => childId) ?? []),
        ])
        ?.flat(2) ?? []
    );
  }

  const currentValues =
    (selectedDomains?.map(({ value }) => value) as string[]) ?? [];

  return currentValues;
};
